import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { AXIS, COMPACT_CATEGORY_TICK, resolveAxisWidth } from '../nodes/nodeChartTheme';
import { useIsMobile } from '../../hooks/useMediaQuery';
import {
  buildAverageCaption,
  buildComparisonBars,
  DEVIATION_LEVEL_ORDER,
  DEVIATION_LEVELS,
  formatScore,
  TOLERANCE_LABEL
} from './qualityBenchmark';

/** Ширина знака подписи (11px, полужирный) с запасом: подложке лучше быть шире текста. */
const LABEL_CHAR_WIDTH = 6.6;

/**
 * Подпись у конца столбца — на подложке цвета карточки. Столбец, который кончается чуть левее
 * среднего, ставит подпись прямо на пунктир, и без подложки линия перечёркивала цифры.
 * Обводка вокруг букв не помогает: линия остаётся видна в просветах между знаками. Подписи
 * recharts рисует слоем выше линии ориентира (2000 против 400), поэтому подложка её закрывает.
 */
const renderLabel = ({ x, y, width, height, value }) => {
  const text = value === null || value === undefined ? '' : String(value);
  if (!text) {
    return null;
  }

  const textX = (x ?? 0) + (width ?? 0) + 6;
  const textY = (y ?? 0) + (height ?? 0) / 2;

  return (
    <g>
      <rect
        x={textX - 3}
        y={textY - 8}
        width={text.length * LABEL_CHAR_WIDTH + 6}
        height={16}
        rx={3}
        fill="hsl(var(--card))"
      />
      <text
        x={textX}
        y={textY}
        textAnchor="start"
        dominantBaseline="middle"
        fill="hsl(var(--foreground))"
        fontSize="11"
        fontWeight="600"
      >
        {text}
      </text>
    </g>
  );
};

const SCORE_TICKS = [0, 2, 4, 6, 8, 10];
const ROW_HEIGHT = 36;
const MIN_HEIGHT = 200;

/** Линия ориентира: пунктир цвета второстепенного текста — серые столбцы «на уровне» с ней не спутать. */
export const REFERENCE_LINE_PROPS = {
  stroke: 'hsl(var(--muted-foreground))',
  strokeDasharray: '6 4',
  strokeWidth: 1.5
};

const ComparisonTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const bar = payload[0].payload;
  if (!bar) {
    return null;
  }

  const level = bar.level ? DEVIATION_LEVELS[bar.level] : null;

  return (
    <div
      className="rounded-md border px-3 py-2 text-sm shadow-md max-sm:max-w-[220px] max-sm:text-xs"
      style={{
        background: 'hsl(var(--card))',
        color: 'hsl(var(--card-foreground))',
        borderColor: 'hsl(var(--border))'
      }}
    >
      <div className="mb-1 font-medium">{bar.label}</div>
      <div>Балл: {bar.scoreLabel}</div>
      {level && (
        <div>
          {level.symbol} {level.label}: {bar.deltaLabel}
        </div>
      )}
      <div className="text-muted-foreground">Оценённых звонков: {bar.scoredCalls.toLocaleString('ru-RU')}</div>
    </div>
  );
};

/** Образец пунктира для подписей под графиками — тот же штрих, что у линии ориентира. */
export const ReferenceSwatch = () => (
  <span aria-hidden="true" className="inline-block w-5 shrink-0 border-t-2 border-dashed border-muted-foreground" />
);

/**
 * «Сравнение качества»: горизонтальные столбцы среднего балла сотрудников (0..10) и пунктир
 * среднего по всем сотрудникам. Цвет столбца — положение относительно среднего, знак на
 * подписи и легенда дублируют его для тех, кто цвета не различает.
 *
 * `extendDomain` у линии обязателен: в recharts 3 умолчание `discard`, и линия молча
 * пропадает, как только выходит за диапазон оси.
 */
const QualityComparisonChart = ({ comparison, filtered = false }) => {
  const isMobile = useIsMobile();
  const [chartWidth, setChartWidth] = useState(0);

  const bars = useMemo(
    () => buildComparisonBars(comparison, { compact: isMobile }),
    [comparison, isMobile]
  );

  const axisWidth = useMemo(
    () => resolveAxisWidth(bars, { compact: isMobile, containerWidth: chartWidth }),
    [bars, isMobile, chartWidth]
  );

  if (bars.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
        Нет оценённых звонков за период
      </div>
    );
  }

  const average = comparison?.average ?? null;
  const caption = buildAverageCaption(comparison, { filtered });
  const height = Math.max(MIN_HEIGHT, bars.length * ROW_HEIGHT + 56);

  return (
    <>
      <div className="w-full min-w-0" style={{ height }}>
        <ResponsiveContainer
          width="100%"
          height="100%"
          minWidth={0}
          minHeight={160}
          onResize={(width) => setChartWidth(width)}
        >
          <BarChart
            data={bars}
            layout="vertical"
            margin={{
              top: isMobile ? 8 : 22,
              right: isMobile ? AXIS.labelGutterCompact : AXIS.labelGutter,
              left: isMobile ? 0 : 8,
              bottom: 8
            }}
          >
            <CartesianGrid {...AXIS.grid} horizontal={false} />
            <XAxis
              type="number"
              domain={[0, 10]}
              ticks={SCORE_TICKS}
              tick={AXIS.tick}
              tickFormatter={formatScore}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={axisWidth}
              interval={0}
              tick={isMobile ? COMPACT_CATEGORY_TICK : AXIS.tick}
            />
            <Tooltip
              content={<ComparisonTooltip />}
              wrapperStyle={AXIS.tooltipWrapperStyle}
              cursor={{ fillOpacity: 0.1 }}
            />
            <Bar
              dataKey="score"
              radius={AXIS.radiusHorizontal}
              maxBarSize={AXIS.maxBarSize}
              isAnimationActive={false}
            >
              {bars.map((bar) => (
                <Cell key={bar.key} fill={bar.color} />
              ))}
              <LabelList dataKey="valueLabel" content={renderLabel} />
            </Bar>
            {average !== null && (
              <ReferenceLine
                x={average}
                ifOverflow="extendDomain"
                {...REFERENCE_LINE_PROPS}
                label={
                  isMobile
                    ? undefined
                    : {
                        value: `Среднее ${formatScore(average)}`,
                        position: 'top',
                        fontSize: 11,
                        fill: 'hsl(var(--muted-foreground))'
                      }
                }
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" data-quality-legend>
        {DEVIATION_LEVEL_ORDER.map((level) => (
          <li key={level} className="flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: DEVIATION_LEVELS[level].color }}
            />
            <span>
              {DEVIATION_LEVELS[level].symbol} {DEVIATION_LEVELS[level].label}
              {level === 'level' ? ` (${TOLERANCE_LABEL})` : ''}
            </span>
          </li>
        ))}
        {caption && (
          <li className="flex min-w-0 items-center gap-1.5" data-quality-average>
            <ReferenceSwatch />
            <span className="break-words">{caption}</span>
          </li>
        )}
      </ul>
    </>
  );
};

export default QualityComparisonChart;
